import { errorThatWillCauseAlert } from 'owa-trace';
import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import ContextProperty from 'owa-service/lib/contract/ContextProperty';
import contextProperty from 'owa-service/lib/factory/contextProperty';
import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import findPeopleJsonRequest from 'owa-service/lib/factory/findPeopleJsonRequest';
import FindPeopleJsonResponse from 'owa-service/lib/contract/FindPeopleJsonResponse';
import findPeopleOperation from 'owa-service/lib/operation/findPeopleOperation';
import FindPeopleRequest from 'owa-service/lib/contract/FindPeopleRequest';
import findPeopleRequest from 'owa-service/lib/factory/findPeopleRequest';
import folderId from 'owa-service/lib/factory/folderId';
import IndexedPageView from 'owa-service/lib/contract/IndexedPageView';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import PersonaResponseShape from 'owa-service/lib/contract/PersonaResponseShape';
import personaResponseShape from 'owa-service/lib/factory/personaResponseShape';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import Sorting from 'mini-people-common/lib/schema/Sorting';
import SortResults from 'owa-service/lib/contract/SortResults';
import sortResults from 'owa-service/lib/factory/sortResults';
import targetFolderId from 'owa-service/lib/factory/targetFolderId';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import {
    UNIVERSAL_FOLDER_NAME_FOR_USER_CONTACTS,
    DEFAULT_CONTACTS_FOLDER_ID,
} from '../utils/constants';

const findPeoplePromiseCache = {};

export interface FindPeopleArgs {
    queryString: string;
    offset: number;
    maxEntriesReturned: number;
    sorting: Sorting;
    parentFolderId: string;
    skipCache?: boolean;
}

/**
 * Calls the findPeople API to fetch a list of personas corresponding to the passed args.
 * @param {FindPeopleArgs} args - Object containing queryString, offset, and maxEntriesReturned
 * @returns FindPeopleJSONResponse
 */
export default async function findPeople(args: FindPeopleArgs): Promise<FindPeopleJsonResponse> {
    const { queryString, offset, maxEntriesReturned, sorting, parentFolderId, skipCache } = args;
    const findPeopleCacheKey = generateCacheKey(args);
    if (findPeoplePromiseCache[findPeopleCacheKey] && !skipCache) {
        return findPeoplePromiseCache[findPeopleCacheKey];
    }

    const requestBody = configureRequestBody(
        queryString,
        offset,
        maxEntriesReturned,
        sorting,
        parentFolderId
    );
    try {
        const peopleResponsePromise = findPeopleOperation(
            findPeopleJsonRequest({
                Header: getJsonRequestHeader(),
                Body: requestBody,
            })
        );
        if (!skipCache) {
            findPeoplePromiseCache[findPeopleCacheKey] = peopleResponsePromise;
        }
        return await peopleResponsePromise;
    } catch (error) {
        errorThatWillCauseAlert(
            'People.Hub: FetchContacts : FindPeopleOperation failed with error : ' + error.Message
        );
        return null;
    }
}

function generateCacheKey(args: FindPeopleArgs) {
    const { queryString, offset, maxEntriesReturned, sorting, parentFolderId } = args;
    return (
        parentFolderId + '_' + offset + '_' + maxEntriesReturned + '_' + queryString + '_' + sorting
    );
}

function sortOrder(sorting: Sorting): SortResults[] {
    // Currently we are not allowing sorting orders other than byLastName and byFirstName. Hence, setting all others to byFirstName.
    const sortPath: string =
        sorting === Sorting.ByLastName
            ? 'PersonaDisplayNameLastFirst'
            : 'PersonaDisplayNameFirstLast';
    const primarySort = sortResults({
        Order: 'Ascending',
        Path: propertyUri({ FieldURI: sortPath }),
    });
    return [primarySort];
}

function configureRequestBody(
    queryString: string,
    offset: number,
    maxEntriesReturned: number,
    sorting: Sorting,
    parentFolderId: string
): FindPeopleRequest {
    const querySources = ['Mailbox'];
    return findPeopleRequest({
        IndexedPageItemView: configureIndexedPageView(offset, maxEntriesReturned),
        PersonaShape: personaResponseShape(getDefaultShape()),
        QueryString: queryString,
        QuerySources: querySources,
        SearchPeopleSuggestionIndex: false,
        ShouldResolveOneOffEmailAddress: false,
        ParentFolderId: targetFolderId({
            BaseFolderId: getBaseFolderId(parentFolderId),
        }),
        SortOrder: sortOrder(sorting),
        Context: configureContext(),
    });
}

function getBaseFolderId(parentFolderId: string): BaseFolderId {
    return parentFolderId === DEFAULT_CONTACTS_FOLDER_ID
        ? distinguishedFolderId({
              Id: UNIVERSAL_FOLDER_NAME_FOR_USER_CONTACTS as DistinguishedFolderIdName,
          })
        : folderId({
              Id: parentFolderId,
          });
}

function configureIndexedPageView(offset: number, maxEntriesReturned: number): IndexedPageView {
    return indexedPageView({
        BasePoint: 'Beginning',
        Offset: offset,
        MaxEntriesReturned: maxEntriesReturned,
    });
}

function getDefaultShape() {
    const defaultShape: PersonaResponseShape = {
        BaseShape: 'Default',
        AdditionalProperties: [
            propertyUri({ FieldURI: 'PersonaTitle' }),
            propertyUri({ FieldURI: 'PersonaBusinessPhoneNumbers' }),
            propertyUri({ FieldURI: 'PersonaHomePhones' }),
            propertyUri({ FieldURI: 'PersonaMobilePhones' }),
            propertyUri({ FieldURI: 'PersonaDisplayNamePrefix' }),
            propertyUri({ FieldURI: 'PersonaGeneration' }),
            propertyUri({ FieldURI: 'PersonaAttributions' }),
            propertyUri({ FieldURI: 'PersonaEmails1' }),
            propertyUri({ FieldURI: 'PersonaEmails2' }),
            propertyUri({ FieldURI: 'PersonaEmails3' }),
        ],
    };
    return defaultShape;
}

function configureContext(): ContextProperty[] {
    return [contextProperty({ Key: 'AppName', Value: 'OWA' })];
}
